/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react"
import { message, Card, Spin } from "antd"
import { UploadFile } from "antd/lib/upload/interface"
import ErrorState from "@/components/common/ErrorState"
import { useNavigate, useParams } from "react-router"
import { TempLayout } from "@/layouts"
import HeTimesheetForm from "./components/HeTimesheetForm"
import { IHeTimesheetUpdateDto } from "@/types"
import { useHeTimesheetStore } from "@/stores"
import { Moment } from "moment"

const getErrorMessage = (error: unknown): string => {
  const apiMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message
  if (apiMessage) return apiMessage
  if (error instanceof Error) return error.message
  return "An unknown error occurred"
}

interface IHeTimesheetFormValues {
  vendor_name: string
  branch_name: string
  department_name: string
  job_location?: string
  activity_name: string
  hull_name: string
  hull_code: string
  operator_name: string
  operator_id?: string
  shift_type: "PAGI" | "MALAM"
  date_range: [Moment, Moment]
  hm_start: number
  hm_stop: number
  hm_total: number
  depth_meter_from?: number
  depth_meter_to?: number
  depth_meter_total?: number
  note?: string
  hm_start_photo?: UploadFile[]
  hm_stop_photo?: UploadFile[]
  unit_photo?: UploadFile[]
  operator?: UploadFile[]
}

const HeTimesheetsEdit = () => {
  const navigate = useNavigate()
  const { recordId } = useParams()
  const { currentHeTimesheet, fetchHeTimesheetById, updateHeTimesheet, fetchHeTimesheetAttachmentUrl, isLoading, error } = useHeTimesheetStore()
  const [submitting, setSubmitting] = useState(false)
  const [initialValues, setInitialValues] = useState<IHeTimesheetUpdateDto | null>(null)

  useEffect(() => {
    if (recordId) {
      void fetchHeTimesheetById(recordId).catch(err => {
        void message.error(getErrorMessage(err))
      })
    }
  }, [recordId, fetchHeTimesheetById])

  useEffect(() => {
    const setEditInitialValues = async () => {
      if (!currentHeTimesheet) {
        setInitialValues(null)
        return
      }

      const getUploadFile = async (type: string): Promise<UploadFile[] | undefined> => {
        const attachment = currentHeTimesheet.attachments?.find(att => att.type === type)
        if (!attachment?.key) return undefined

        const url = await fetchHeTimesheetAttachmentUrl({ key: attachment.key })
        return [
          {
            uid: `${type}-${attachment.key}`,
            name: `${type}.jpg`,
            status: "done",
            url,
          } as UploadFile,
        ]
      }

      try {
        const [hmStartPhoto, hmStopPhoto, unitPhoto, operatorPhoto] = await Promise.all([
          getUploadFile("hm_start_photo"),
          getUploadFile("hm_stop_photo"),
          getUploadFile("unit_photo"),
          getUploadFile("operator"),
        ])

        setInitialValues({
          vendor_name: currentHeTimesheet.vendor_name,
          branch_name: currentHeTimesheet.branch_name,
          department_name: currentHeTimesheet.department_name,
          job_location: currentHeTimesheet.job_location || currentHeTimesheet.location || currentHeTimesheet.location_timesheet,
          activity_name: currentHeTimesheet.activity_name,
          hull_name: currentHeTimesheet.hull_name,
          hull_code: currentHeTimesheet.hull_code,
          operator_name: currentHeTimesheet.operator_name,
          operator_id: currentHeTimesheet.operator_id,
          shift_type: currentHeTimesheet.shift_type,
          date_from: currentHeTimesheet.date_from,
          date_to: currentHeTimesheet.date_to,
          hm_start: currentHeTimesheet.hm_start,
          hm_stop: currentHeTimesheet.hm_stop,
          hm_total: currentHeTimesheet.hm_total,
          depth_meter_from: currentHeTimesheet.depth_meter_from,
          depth_meter_to: currentHeTimesheet.depth_meter_to,
          depth_meter_total: currentHeTimesheet.depth_meter_total,
          note: currentHeTimesheet.note,
          hm_start_photo: hmStartPhoto as any,
          hm_stop_photo: hmStopPhoto as any,
          unit_photo: unitPhoto as any,
          operator: operatorPhoto as any,
        })
      } catch (err) {
        void message.error(getErrorMessage(err))
      }
    }

    void setEditInitialValues()
  }, [currentHeTimesheet, fetchHeTimesheetAttachmentUrl])

  const handleSubmit = async (values: IHeTimesheetFormValues) => {
    if (!recordId) return
    setSubmitting(true)
    try {
      const getFile = (fileField?: UploadFile[]) => (fileField && fileField.length > 0 ? (fileField[0].originFileObj as File) : undefined)
      const [startDate, endDate] = values.date_range || []
      const payload: IHeTimesheetUpdateDto = {
        vendor_name: values.vendor_name,
        branch_name: values.branch_name,
        job_location: values.job_location || "",
        activity_name: values.activity_name,
        hull_name: values.hull_name,
        hull_code: values.hull_code || "",
        operator_name: values.operator_name,
        operator_id: values.operator_id || "",
        shift_type: values.shift_type,
        date_from: startDate?.toISOString(),
        date_to: endDate?.toISOString(),
        hm_start: values.hm_start,
        hm_stop: values.hm_stop,
        hm_total: values.hm_total,
        depth_meter_from: values.depth_meter_from || 0,
        depth_meter_to: values.depth_meter_to || 0,
        depth_meter_total: values.depth_meter_total || 0,
        note: values.note || "",
      }

      const hmStartPhoto = getFile(values.hm_start_photo)
      const hmStopPhoto = getFile(values.hm_stop_photo)
      const unitPhoto = getFile(values.unit_photo)
      const operatorPhoto = getFile(values.operator)

      if (hmStartPhoto) payload.hm_start_photo = hmStartPhoto
      if (hmStopPhoto) payload.hm_stop_photo = hmStopPhoto
      if (unitPhoto) payload.unit_photo = unitPhoto
      if (operatorPhoto) payload.operator = operatorPhoto

      await updateHeTimesheet(payload, recordId)
      void message.success("He Timesheet updated successfully!")
      navigate(-1)
    } catch (err: unknown) {
      void message.error(getErrorMessage(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <TempLayout title="HE Timesheet - Edit Entry" navNavigateUrl="/hets/history">
      <Card>
        <Spin spinning={isLoading || submitting}>
          {error ? (
            <ErrorState code={error as any} message={String(error)} onAction={() => window.location.reload()} actionLabel="Retry" />
          ) : initialValues ? (
            <HeTimesheetForm isEditMode={true} initialValues={initialValues} loading={isLoading || submitting} onFinish={(values: any) => void handleSubmit(values as IHeTimesheetFormValues)} />
          ) : null}
        </Spin>
      </Card>
    </TempLayout>
  )
}

export default HeTimesheetsEdit
